// WalletConnect v2 integration for mobile and desktop wallet support
import { EthereumProvider } from '@walletconnect/ethereum-provider';
import { WalletConnectionError } from './walletConnectors';
import { getChainRPCUrl } from './chainRegistry';
import { toast } from 'sonner';

export interface WalletConnectConfig {
  projectId: string;
  chains: number[];
  optionalChains?: number[];
  rpcMap?: Record<number, string>;
}

// Default configuration - WalletConnect Cloud Project ID configured
const DEFAULT_CONFIG: WalletConnectConfig = {
  projectId: import.meta.env.VITE_WALLETCONNECT_PROJECT_ID || '00bac352e71e1e141413ca1edd9335c6',
  chains: [1], // Ethereum Mainnet
  optionalChains: [1],
  rpcMap: {
    1: getChainRPCUrl('ethereum') || 'https://eth.llamarpc.com', // Use chain registry, fallback to public RPC
  },
};

let walletConnectProvider: EthereumProvider | null = null;
let isInitializing = false; // Guard against concurrent initialization

export async function initializeWalletConnect(config?: Partial<WalletConnectConfig>): Promise<EthereumProvider> {
  // Return existing provider if already connected
  if (walletConnectProvider && walletConnectProvider.connected) {
    return walletConnectProvider;
  }

  // Prevent concurrent initialization
  if (isInitializing) {
    // Wait for ongoing initialization to complete
    while (isInitializing) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    if (walletConnectProvider) {
      return walletConnectProvider;
    }
  }

  // Check if already initialized but not connected
  if (walletConnectProvider) {
    return walletConnectProvider;
  }

  isInitializing = true;

  try {
    const finalConfig = { ...DEFAULT_CONFIG, ...config };
    
    if (finalConfig.projectId === 'YOUR_PROJECT_ID_HERE') {
      throw new WalletConnectionError(
        'WalletConnect project ID not configured. Please set VITE_WALLETCONNECT_PROJECT_ID in your environment variables.',
        'CONFIG_ERROR'
      );
    }

    walletConnectProvider = await EthereumProvider.init({
      projectId: finalConfig.projectId,
      chains: finalConfig.chains,
      optionalChains: finalConfig.optionalChains,
      rpcMap: finalConfig.rpcMap,
      metadata: {
        name: 'KawSwap DEX',
        description: 'Decentralized Exchange on Internet Computer',
        url: window.location.origin,
        icons: [
          `${window.location.origin}/vite.svg`,
          `${window.location.origin}/favicon.ico`,
          'https://kawswap.io/favicon.ico',
        ],
      },
      showQrModal: true, // Enable built-in QR modal for better mobile support and deep linking
      disableProviderPing: false,
      // Peak mobile optimization settings
      qrModalOptions: {
        themeMode: 'dark',
        themeVariables: {
          '--w3m-z-index': '9999',
        },
        enableExplorer: true, // Show wallet explorer for better mobile app discovery
        explorerRecommendedWalletIds: [
          '4622a2b2d6af1c9844944291e5e7351a6aa24cd7b23099efac1b2fd875da31a0', // Trust Wallet (FIRST - ADMIN WALLET - Multi-chain support)
          'c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96', // MetaMask
          'fd20dc426fb37566d803205b19bbc1d4096b248ac04548e3cfb6b3a38bd033aa8', // Coinbase Wallet
          'c482dfe368d4f004479977fd88e80dc9e81107f3245d706811581a6dfe69c534', // NOW Wallet - Supports both EVM and UTXO (including Ravencoin) via WalletConnect
        ],
        // Force Trust Wallet as the default wallet for deep linking
        // This helps iOS prioritize Trust Wallet when multiple wallets are installed
        walletConnectUri: undefined, // Will be set when user selects Trust Wallet
      },
    });

    isInitializing = false;
    return walletConnectProvider;
  } catch (error: any) {
    isInitializing = false;
    walletConnectProvider = null; // Reset on error
    
    // Handle specific WalletConnect errors
    if (error?.message?.includes('frames-disallowed') || error?.message?.includes('dapp.frames-disallowed')) {
      throw new WalletConnectionError(
        'WalletConnect configuration error: Domain not whitelisted. Please add your domain to WalletConnect Cloud dashboard at https://cloud.walletconnect.com',
        'FRAMES_DISALLOWED'
      );
    }
    if (error?.message?.includes('projectId') || error?.message?.includes('Project ID')) {
      throw new WalletConnectionError(
        'WalletConnect project ID is invalid or not configured. Please set VITE_WALLETCONNECT_PROJECT_ID in your environment variables.',
        'INVALID_PROJECT_ID'
      );
    }
    throw new WalletConnectionError(
      `Failed to initialize WalletConnect: ${error?.message || 'Unknown error'}`,
      'INIT_ERROR'
    );
  }
}

export async function connectWalletConnect(): Promise<{ address: string; chainId: string }> {
  try {
    // Detect if we're on mobile
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    // On mobile, prefer WalletConnect for better app integration
    // On desktop, try extension first for speed
    if (!isMobile && window.ethereum) {
      try {
        // Try to use browser extension directly (faster than WalletConnect for desktop)
        const accounts = await Promise.race([
          window.ethereum.request({ method: 'eth_requestAccounts' }),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 5000)),
        ]) as string[];
        
        if (accounts && accounts.length > 0) {
          const chainId = await window.ethereum.request({ method: 'eth_chainId' }) as string;
          return {
            address: accounts[0],
            chainId: chainId,
          };
        }
      } catch (extError: any) {
        // If extension connection fails, fall through to WalletConnect
        if (extError.code === 4001) {
          throw new WalletConnectionError('User rejected the connection request.', 'USER_REJECTED');
        }
        // Continue to WalletConnect for mobile/other cases
      }
    }

    // Use WalletConnect v2 (better for mobile, works for desktop too)
    const provider = await initializeWalletConnect();

    // Peak mobile optimization: QR modal shows immediately, deep linking enabled
    // On mobile, the built-in modal handles universal links and deep linking automatically
    // This provides the fastest mobile connection experience
    try {
      // For mobile, use much longer timeout to allow for:
      // - QR code scanning
      // - App switching (especially if user needs to manually open Trust Wallet)
      // - Navigating to WalletConnect in wallet app
      // - Approving connection
      // - Deep linking back to browser
      // For desktop, longer timeout to account for user interaction delays
      const enableTimeout = isMobile ? 120000 : 90000; // 2 minutes mobile, 90 seconds desktop
      
      await Promise.race([
        provider.enable(),
        new Promise((_, reject) => 
          setTimeout(() => reject(new WalletConnectionError('Connection timeout. Please try again.', 'TIMEOUT')), enableTimeout)
        ),
      ]);
    } catch (enableError: any) {
      // Mobile-specific: If enable fails but provider is connecting, wait for deep link
      if (isMobile && provider.uri) {
        // Give extra time for mobile deep linking to complete (app switching can take time)
        // This handles the case where user scans QR and switches to wallet app
        // Increased to 10s to allow for manual wallet app opening and navigation
        await new Promise(resolve => setTimeout(resolve, 10000)); // 10 seconds for mobile deep linking
        
        // Check if connection completed via deep link
        if (provider.accounts && provider.accounts.length > 0) {
          // Connection succeeded via deep link - mobile optimization worked!
        } else {
          // Still no connection - throw error
          throw enableError;
        }
      } else {
        // Desktop or no URI - throw immediately
        throw enableError;
      }
    }

    if (!provider.accounts || provider.accounts.length === 0) {
      throw new WalletConnectionError('No accounts found. Please connect your wallet.', 'NO_ACCOUNTS');
    }

    const address = provider.accounts[0];
    const chainId = `0x${provider.chainId.toString(16)}`;

    return { address, chainId };
  } catch (error: any) {
    if (error instanceof WalletConnectionError) {
      throw error;
    }
    if (error?.code === 4001) {
      throw new WalletConnectionError('User rejected the connection request.', 'USER_REJECTED');
    }
    // Handle frames-disallowed error specifically
    if (error?.message?.includes('frames-disallowed') || error?.message?.includes('dapp.frames-disallowed')) {
      throw new WalletConnectionError(
        'WalletConnect error: Domain not whitelisted. Please add kawswap.io to your WalletConnect Cloud project at https://cloud.walletconnect.com',
        'FRAMES_DISALLOWED'
      );
    }
    throw new WalletConnectionError(
      error?.message || 'Failed to connect via WalletConnect',
      error?.code || 'CONNECTION_FAILED'
    );
  }
}

export function getWalletConnectProvider(): EthereumProvider | null {
  return walletConnectProvider;
}

export async function disconnectWalletConnect(): Promise<void> {
  if (walletConnectProvider && walletConnectProvider.connected) {
    await walletConnectProvider.disconnect();
    walletConnectProvider = null;
  }
}

export function isWalletConnectConnected(): boolean {
  return walletConnectProvider?.connected ?? false;
}

export function getWalletConnectQRUri(): string | null {
  return walletConnectProvider?.uri ?? null;
}

export function onWalletConnectDisplayUri(callback: (uri: string) => void): (() => void) | void {
  if (walletConnectProvider) {
    walletConnectProvider.on('display_uri', (uri) => {
      callback(uri);
      // CRITICAL iOS DEEP LINKING ISSUE:
      // iOS automatically opens the first compatible wallet app when scanning QR codes.
      // Even if you select Trust Wallet in the explorer, iOS may still open MetaMask.
      // Users MUST manually cancel and select Trust Wallet in their wallet app.
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      if (isIOS) {
        toast.warning('⚠️ iOS Deep Linking Issue', {
          description: 'If MetaMask opens automatically, CANCEL it. Then manually open Trust Wallet app → Settings → WalletConnect → New Connection → Scan QR code. Trust Wallet is required for admin access.',
          duration: 20000,
        });
      } else {
        toast.info('Select Trust Wallet', {
          description: 'If MetaMask opens automatically, cancel and manually select Trust Wallet from the list. Trust Wallet supports more blockchains and is required for admin access.',
          duration: 15000,
        });
      }
    });
    return () => {
      walletConnectProvider?.removeListener('display_uri', callback);
    };
  }
}

export function onWalletConnectAccountsChanged(callback: (accounts: string[]) => void): (() => void) | void {
  if (walletConnectProvider) {
    walletConnectProvider.on('accountsChanged', callback);
    return () => {
      walletConnectProvider?.removeListener('accountsChanged', callback);
    };
  }
}

export function onWalletConnectChainChanged(callback: (chainId: number) => void): (() => void) | void {
  if (walletConnectProvider) {
    walletConnectProvider.on('chainChanged', callback);
    return () => {
      walletConnectProvider?.removeListener('chainChanged', callback);
    };
  }
}

export function onWalletConnectDisconnect(callback: () => void): (() => void) | void {
  if (walletConnectProvider) {
    walletConnectProvider.on('disconnect', callback);
    return () => {
      walletConnectProvider?.removeListener('disconnect', callback);
    };
  }
}

export function onWalletConnectConnect(callback: () => void): (() => void) | void {
  if (walletConnectProvider) {
    // WalletConnect v2 emits 'connect' event when connection is established
    walletConnectProvider.on('connect', callback);
    return () => {
      walletConnectProvider?.removeListener('connect', callback);
    };
  }
}

// Export UTXO signing functions
export { 
  initializeUTXOProvider,
  connectUTXOProvider,
  getUTXOProvider,
  signUTXOTransaction,
  checkUTXOSupport,
  getUTXOAddresses
} from './utxoWalletSigner';

